import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  LinkedinFilled,
  MailOutlined,
  CopyOutlined,
  ThunderboltOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { Spin, message, Tooltip } from 'antd';
import styles from '../InternalExternalDetail.module.css';
import ProfileMetaList from './ProfileMetaList';
import Button from './Button';
import InviteTalentModal from '../InviteTalentModal/InviteTalentModal';
import SendReminderModal from '../../ManageInvites/SendReminderModal';
import { fetchExternalContactInit, getExternalUserDetailInit } from '../../../redux/actions';
import ExpIcon from '../../../assets/icons/ExpIcon.svg'
import LocIcon from '../../../assets/icons/LocIcon.svg'
import InviteTalent from '../../../assets/icons/InviteTalent.svg'
import { canUseSubscriptionFeatures } from '../../../shared/billing';

const PhoneIcon = () => (
  <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.26855 0.5H4.76855C4.95229 0.500011 5.07991 0.55472 5.18457 0.65332C5.27807 0.741412 5.35658 0.855859 5.41504 1.00684L5.4668 1.16992L6.13184 4.18359C6.1645 4.41538 6.15698 4.60038 6.12207 4.74707C6.09092 4.87804 6.02914 4.98631 5.92969 5.08105L5.92285 5.08691L3.40527 7.57031L3.12598 7.8457L3.32617 8.18164C3.75291 8.89877 4.20639 9.57129 4.68652 10.1973C5.16934 10.8268 5.70553 11.4248 6.29395 11.9922C6.90732 12.6254 7.54853 13.2006 8.21777 13.7158C8.89293 14.2356 9.59837 14.6906 10.334 15.0801L10.665 15.2549L10.9258 14.9873L13.3311 12.5195L13.3408 12.5088C13.4804 12.3574 13.627 12.2663 13.7832 12.2197C13.9142 12.1809 14.044 12.1673 14.1748 12.1768L14.3066 12.1943L17.127 12.8184C17.2927 12.8645 17.4245 12.9359 17.5312 13.0303L17.6309 13.1328C17.7476 13.2738 17.8047 13.4317 17.8047 13.626V17.0361C17.8047 17.2731 17.7305 17.4447 17.5869 17.5869C17.4405 17.7319 17.2676 17.8047 17.0361 17.8047C15.161 17.8046 13.2407 17.3544 11.2715 16.4395C9.30334 15.525 7.47245 14.2244 5.7793 12.5312C4.19223 10.9442 2.9492 9.23511 2.04492 7.4043L1.86816 7.03613C0.951129 5.06465 0.5 3.14535 0.5 1.27441C0.500013 1.09656 0.541272 0.954083 0.623047 0.833008L0.71875 0.71875C0.862804 0.573905 1.03455 0.5 1.26855 0.5ZM1.21582 1.73047C1.24675 2.44345 1.35877 3.19177 1.54883 3.97363C1.7404 4.76166 2.04011 5.63145 2.44434 6.58008L2.7334 7.25879L3.25684 6.73828L5.27637 4.73047L5.46973 4.53809L5.41211 4.27148L4.84375 1.60449L4.75879 1.20898H1.19336L1.21582 1.73047ZM17.0957 13.5322L16.6973 13.4502L14.2119 12.9355L13.9404 12.8799L13.748 13.0801L11.7852 15.1377L11.3037 15.6416L11.9365 15.9365C12.6314 16.2598 13.3848 16.5217 14.1943 16.7246C15.005 16.9277 15.7972 17.0497 16.5703 17.0889L17.0957 17.1152V13.5322Z" fill="#002D40" stroke="#002D40"/>
  </svg>
);
const getCreditsPayload = (cb) =>
  cb?.data && typeof cb.data === 'object' ? cb.data : cb || {};

const isPlanExpired = (cb) => {
  const p = getCreditsPayload(cb);
  const status = String(p?.status || '').toLowerCase();
  if (['expired', 'cancelled', 'canceled', 'inactive', 'past_due', 'unpaid'].includes(status)) return true;

  return p?.hasActivePlan === false && Boolean(p?.planCode);
};

const ContactRow = ({ icon, text, href, onCopy }) => (
  <div className={styles.contactRow}>
    {href ? (
      <a href={href} target="_blank" rel="noreferrer" className={styles.contactLink} style={{ textDecoration: 'underline' }}>
        <span className={styles.contactIcon}>{icon}</span>
        <span className={styles.contactText}>{text}</span>
      </a>
    ) : (
      <span className={styles.contactLink}>
        <span className={styles.contactIcon}>{icon}</span>
        <span className={styles.contactText}>{text}</span>
      </span>
    )}
    {onCopy && (
      <button className={styles.copyBtn} onClick={onCopy}>
        <CopyOutlined />
      </button>
    )}
  </div>
);

const ExternalProfile = ({ id }) => {
  const dispatch = useDispatch();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);

  const { data, rawData, contactData, contactLoading, contactError, contactErrorMessage } = useSelector((state) => state.externalUserDetailReducer);

  useEffect(() => {
    if (contactError && contactErrorMessage) {
      message.error(contactErrorMessage);
    }
  }, [contactError, contactErrorMessage]);
  const creditsBalance = useSelector((state) => state.plansReducer?.creditsBalance);
  const creditsBalanceLoaded = useSelector((state) => state.plansReducer?.creditsBalanceLoaded);
  const subscriptionFeaturesEnabled = canUseSubscriptionFeatures();

  const {
    name = '',
    title = '',
    avatar: avatarSrc = '',
    initials = '?',
    aiScore,
    experience = '',
    location = '',
    inviteStatus = null,
    contactInfo,
    linkedinUrl,
  } = data || {};

  const linkedin = contactData?.linkedin_url || linkedinUrl || '';
  const contactAvailability = contactData?.contact_availability;
  const rawEmails = contactData?.contact_info?.emails || contactInfo?.emails || [];
  const rawPhones = contactData?.contact_info?.phones || contactInfo?.phones || [];
  const emails = contactAvailability
    ? rawEmails.filter((_, idx) =>
        idx === 0
          ? contactAvailability.work_email !== false
          : idx === 1
          ? contactAvailability.personal_email !== false
          : true
      )
    : rawEmails;
  const phones = contactAvailability
    ? (contactAvailability.phone !== false ? rawPhones : [])
    : rawPhones;

  const formattedLinkedin = /^https?:\/\//.test(linkedin) ? linkedin : `https://${linkedin}`;

  const hasContactInfo = !!(linkedin || emails?.length || phones?.length);
  const hasInviteChannels = !!(emails?.length || phones?.length);
  // contactFetched = true when: fetch API returned (even empty response) OR profile already has contact info
  const contactFetched = !!contactData || hasContactInfo;

  const candidate = {
    name,
    role: title,
    avatar: avatarSrc,
    initials,
    score: aiScore ? `AI Score ${aiScore}%` : '',
    tag: 'External',
    emails,
    phones,
  };

  const profileStats = [
  { icon: <img src={ExpIcon} alt="experience" />, label: 'Experience', value: experience },
  { icon: <img src={LocIcon} alt="location" />, label: 'Location', value: location },
];

  const reminderItem = {
    ...rawData,
    profileId: rawData?.profileId || id,
    profileName: name,
    talentTitle: title,
    ...(emails?.length ? { emails } : {}),
    ...(phones?.length ? { phones } : {}),
    profilePictureUrl: avatarSrc,
    jobId: rawData?.jobId || inviteStatus?.jobId,
    jobTitle: rawData?.jobTitle || inviteStatus?.jobTitle,
    reminderCount: rawData?.reminderCount ?? inviteStatus?.reminderCount ?? 0,
  };

  const planExpired = subscriptionFeaturesEnabled && creditsBalanceLoaded && isPlanExpired(creditsBalance);

  const isReminderDisabled = inviteStatus?.setReminderDisabled || !inviteStatus?.setReminderEnabled;
  const invitedMessage = inviteStatus?.invitedBy?.message;

  const handleFetchContact = () => {
    if (planExpired) {
      return;
    }

    dispatch(fetchExternalContactInit(id));
  };

  const copy = (text, label) =>
    navigator.clipboard?.writeText(text).then(() => message.success(`${label} copied to clipboard`))
      .catch(() => message.error('Copy failed'));

  return (
    <>
      <aside className={styles.profileCard}>
        <div className={styles.profileHeaderExternal} />
        <div className={styles.profileBody}>
          <div className={styles.profileTop}>
            <div className={styles.avatar}>
              {avatarSrc && (
                <img
                  src={avatarSrc}
                  alt={name}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    const el = e.currentTarget.nextSibling
                    if (el) el.style.display = 'flex'
                  }}
                />
              )}
              <div className={styles.avatarPlaceholder} style={{ display: avatarSrc ? 'none' : 'flex' }}>
                {initials}
              </div>
            </div>

            <div className={styles.profileInfo}>
              <h2 className={styles.profileName}>{name}</h2>
              <p className={styles.profileRole}>{title}</p>
              <div className={styles.badgeRow}>
                {aiScore && (
                  <span className={styles.aiScore}>
                    <ThunderboltOutlined style={{ marginRight: 6 }} />
                    AI Score {aiScore}%
                  </span>
                )}
                <span className={styles.externalBadge}>External</span>
              </div>
            </div>
          </div>

          <ProfileMetaList items={profileStats} />

          {!!(linkedin || emails?.length || phones?.length) && (
            <div className={`${styles.contactList} ${styles.contactReveal}`}>
              {linkedin && (
                <ContactRow icon={<LinkedinFilled />} text={formattedLinkedin.replace(/^https?:\/\//, '')} href={formattedLinkedin} />
              )}
              {emails?.map((email, i) => (
                <ContactRow key={i} icon={<MailOutlined />} text={email} onCopy={() => copy(email, 'Email')} />
              ))}
              {phones?.map((p, i) => (
                <ContactRow key={i} icon={<PhoneIcon />} text={p} onCopy={() => copy(p, 'Phone Number')} />
              ))}
            </div>
          )}

          <div className={`${styles.actionBlock} ${contactFetched ? styles.contactReveal : ''}`}>
            {!contactFetched ? (
              <Button
                variant="secondaryBtn"
                style={{ width: '100%' }}
                disabled={contactLoading || planExpired}
                onClick={handleFetchContact}
              >
                {contactLoading ? (
                  <Spin size="small" />
                ) : (
                  <>
                    <ThunderboltOutlined style={{ marginRight: 8 }} />
                    Fetch Contact Info and Details
                  </>
                )}
              </Button>
            ) : (
              <>
                {!inviteStatus?.invited ? (
                  <Tooltip
                    placement="bottom"
                    title={!hasInviteChannels ? 'No contact information available. This talent cannot be invited by Email, SMS, or WhatsApp.' : ''}
                  >
                    <span style={{ width: '100%' }}>
                      <Button
                        variant="primaryBtn"
                        disabled={planExpired || !hasInviteChannels}
                        onClick={() => {
                          if (!planExpired && hasInviteChannels) {
                            setShowInviteModal(true);
                          }
                        }}
                      >
                        <img src={InviteTalent} alt="Invite Talent for PMT" />
                        Invite Talent For PMT
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip placement="bottom" title={isReminderDisabled ? inviteStatus?.reminderMessage || 'Reminder not available' : ''}>
                      <span style={{ width: '100%' }}>
                        <Button
                          variant="primaryBtn"
                          disabled={isReminderDisabled}
                          onClick={() => setShowReminderModal(true)}
                        >
                          <BellOutlined style={{ marginRight: 8 }} />
                          Send Reminder
                        </Button>
                      </span>
                    </Tooltip>
                    {!!invitedMessage && <div className={styles.invitedMessage}>{invitedMessage}</div>}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </aside>

      {showInviteModal && <InviteTalentModal candidate={candidate} onClose={() => setShowInviteModal(false)} />}
      {showReminderModal && (
        <SendReminderModal
          item={reminderItem}
          onClose={() => setShowReminderModal(false)}
          onSent={() => dispatch(getExternalUserDetailInit(id))}
          hideTemplateSelect
        />
      )}
    </>
  );
};

export default ExternalProfile;
